/**
 * Geometry helpers for the products map view.
 *
 * `bounds` is the object emitted by google-map-react `onChange` and exposes the
 * four corners of the visible frame as `{ nw, ne, se, sw }`, each `{ lat, lng }`.
 *
 * These helpers stay framework-agnostic (no google.maps dependency) so they can
 * be unit-tested in isolation and reused by both the SmartDP and HotelOnly maps.
 */

/**
 * Returns true when `point` falls inside the visible map frame.
 *
 * Latitude is a simple south <= lat <= north check. Longitude handles the
 * antimeridian: when the frame crosses +/-180 the western edge is greater than
 * the eastern edge, so the inside test becomes "outside the [east, west] gap".
 *
 * @param {{ lat: number, lng: number }} point
 * @param {{ nw: { lat: number, lng: number }, se: { lat: number, lng: number } }} bounds
 * @returns {boolean}
 */
export const isPointInBounds = (point, bounds) => {
	if (!point || !bounds || !bounds.nw || !bounds.se) {
		return false;
	}

	const { lat, lng } = point;

	if (typeof lat !== "number" || Number.isNaN(lat)) {
		return false;
	}
	if (typeof lng !== "number" || Number.isNaN(lng)) {
		return false;
	}

	const north = bounds.nw.lat;
	const south = bounds.se.lat;
	const west = bounds.nw.lng;
	const east = bounds.se.lng;

	const isWithinLatitude = lat <= north && lat >= south;

	if (!isWithinLatitude) {
		return false;
	}

	const crossesAntimeridian = west > east;

	if (crossesAntimeridian) {
		return lng >= west || lng <= east;
	}

	return lng >= west && lng <= east;
};

/**
 * Computes the smallest box enclosing every point.
 *
 * @param {Array<{ lat: number, lng: number }>} points
 * @returns {{ north: number, south: number, east: number, west: number } | null}
 *          null when no valid point is provided.
 */
export const getBoundsOfPoints = (points = []) => {
	const validPoints = points.filter(
		point =>
			point &&
			typeof point.lat === "number" &&
			!Number.isNaN(point.lat) &&
			typeof point.lng === "number" &&
			!Number.isNaN(point.lng)
	);

	if (validPoints.length === 0) {
		return null;
	}

	let north = validPoints[0].lat;
	let south = validPoints[0].lat;
	let east = validPoints[0].lng;
	let west = validPoints[0].lng;

	validPoints.forEach(point => {
		north = Math.max(north, point.lat);
		south = Math.min(south, point.lat);
		east = Math.max(east, point.lng);
		west = Math.min(west, point.lng);
	});

	return { north, south, east, west };
};
